import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  Download,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  RotateCw,
  Maximize2,
  Minimize2,
  FileText,
  FileSpreadsheet,
  FileCode,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  Copy,
  Check,
  Table,
  Code,
  Search,
  Eye,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';

export interface PreviewableFile {
  id?: string;
  originalName?: string;
  fileName?: string;
  name?: string;
  fileSize?: number;
  size?: number;
  mimeType?: string;
  url?: string;
  previewUrl?: string;
  downloadUrl?: string;
  uploaderName?: string;
  createdAt?: string;
}

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: PreviewableFile | null;
}

// The viewer is a separate component so it only mounts while a file is open. Returning early
// from inside it, before its later hooks, made React throw "rendered more hooks" (#310).
export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({ isOpen, onClose, file }) => {
  if (!isOpen || !file) return null;
  return <DocumentPreviewContent isOpen={isOpen} onClose={onClose} file={file} />;
};

const DocumentPreviewContent: React.FC<Omit<DocumentPreviewModalProps, 'file'> & { file: PreviewableFile }> = ({
  isOpen,
  onClose,
  file,
}) => {
  const { token } = useAuth();
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [isLoadingText, setIsLoadingText] = useState(false);
  const [textError, setTextError] = useState<string | null>(null);
  const [csvViewMode, setCsvViewMode] = useState<'table' | 'raw'>('table');
  const [csvSearch, setCsvSearch] = useState('');

  // Reset viewport state when opening a new file
  useEffect(() => {
    if (isOpen && file) {
      setZoom(100);
      setRotation(0);
      setCopied(false);
      setTextContent(null);
      setTextError(null);
      setCsvSearch('');
      setCsvViewMode('table');
    }
  }, [isOpen, file?.id, file?.originalName, file?.fileName, file?.name]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const fileName = file.originalName || file.fileName || file.name || 'Untitled Document';
  const ext = fileName.includes('.') ? fileName.split('.').pop()!.toLowerCase() : '';
  const fileSize = file.fileSize || file.size || 0;

  // Build authentic URLs
  const fileId = file.id;
  let rawPreviewUrl = file.previewUrl || file.url || (fileId ? `/api/files/${fileId}/preview` : '');
  if (rawPreviewUrl.includes('/download')) {
    rawPreviewUrl = rawPreviewUrl.replace('/download', '/preview');
  }

  // Append token for standalone iframe / image / media viewing if not already present
  const previewUrl = rawPreviewUrl
    ? `${rawPreviewUrl}${token ? (rawPreviewUrl.includes('?') ? `&token=${encodeURIComponent(token)}` : `?token=${encodeURIComponent(token)}`) : ''}`
    : '';

  const downloadUrl = file.downloadUrl || (fileId ? `/api/files/${fileId}/download` : file.url || '#');

  // Categorize document format
  const isPdf = ext === 'pdf' || (file.mimeType && file.mimeType.includes('pdf'));
  const isImage =
    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico', 'heic', 'avif'].includes(ext) ||
    Boolean(file.mimeType && file.mimeType.startsWith('image/'));
  const isVideo =
    ['mp4', 'webm', 'ogg', 'mov', 'mkv'].includes(ext) ||
    Boolean(file.mimeType && file.mimeType.startsWith('video/'));
  const isAudio =
    ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'].includes(ext) ||
    Boolean(file.mimeType && file.mimeType.startsWith('audio/'));
  const isCsv = ext === 'csv';
  const isJson = ext === 'json';
  const isTextLike =
    isCsv ||
    isJson ||
    [
      'txt',
      'md',
      'markdown',
      'log',
      'env',
      'js',
      'ts',
      'tsx',
      'jsx',
      'html',
      'htm',
      'css',
      'xml',
      'yaml',
      'yml',
      'sql',
      'py',
      'sh',
      'bash',
    ].includes(ext) ||
    Boolean(file.mimeType && (file.mimeType.startsWith('text/') || file.mimeType.includes('json')));

  const isOfficeDoc = ['docx', 'doc', 'dotx'].includes(ext);
  const isOfficeSheet = ['xlsx', 'xls', 'csv'].includes(ext);
  const isOfficeSlide = ['pptx', 'ppt'].includes(ext);
  const isArchive = ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext);

  // Fetch text contents if text-like
  useEffect(() => {
    if (isTextLike && previewUrl && !textContent && !isLoadingText) {
      setIsLoadingText(true);
      setTextError(null);
      fetch(previewUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to load file content (${res.status})`);
          return res.text();
        })
        .then((data) => {
          setTextContent(data);
          setIsLoadingText(false);
        })
        .catch((err) => {
          setTextError(err.message || 'Unable to load file preview.');
          setIsLoadingText(false);
        });
    }
  }, [isTextLike, previewUrl, textContent, isLoadingText, token]);

  const formatFileSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleCopy = () => {
    if (textContent) {
      navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Icon & Theme Selection
  const getFileIcon = () => {
    if (isPdf) return <FileText className="w-5 h-5 text-rose-500" />;
    if (isImage) return <FileImage className="w-5 h-5 text-sky-500" />;
    if (isOfficeSheet || isCsv) return <FileSpreadsheet className="w-5 h-5 text-emerald-500" />;
    if (isOfficeSlide) return <FileText className="w-5 h-5 text-amber-500" />;
    if (isOfficeDoc) return <FileText className="w-5 h-5 text-blue-500" />;
    if (isVideo) return <FileVideo className="w-5 h-5 text-purple-500" />;
    if (isAudio) return <FileAudio className="w-5 h-5 text-pink-500" />;
    if (isTextLike) return <FileCode className="w-5 h-5 text-amber-600 dark:text-amber-400" />;
    if (isArchive) return <FileArchive className="w-5 h-5 text-indigo-500" />;
    return <FileText className="w-5 h-5 text-brand-500" />;
  };

  // Parse CSV rows
  const parsedCsvRows = React.useMemo(() => {
    if (!isCsv || !textContent) return [];
    const lines = textContent
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    return lines.map((line) => {
      // Simple CSV splitter handling quoted cells
      const row: string[] = [];
      let inQuotes = false;
      let cur = '';
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          row.push(cur.trim());
          cur = '';
        } else {
          cur += char;
        }
      }
      row.push(cur.trim());
      return row;
    });
  }, [isCsv, textContent]);

  const filteredCsvRows = React.useMemo(() => {
    if (!parsedCsvRows || parsedCsvRows.length === 0) return [];
    if (!csvSearch.trim()) return parsedCsvRows;
    const term = csvSearch.toLowerCase();
    const [header, ...body] = parsedCsvRows;
    const matchedBody = body.filter((row) =>
      row.some((cell) => cell.toLowerCase().includes(term))
    );
    return [header, ...matchedBody];
  }, [parsedCsvRows, csvSearch]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-surface-950/75 backdrop-blur-md transition-opacity animate-fade-in"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div
        className={`relative w-full ${
          isFullscreen ? 'w-[98vw] h-[96vh]' : 'max-w-5xl h-[86vh]'
        } bg-white dark:bg-surface-900 rounded-3xl shadow-2xl border border-surface-200 dark:border-surface-800 flex flex-col overflow-hidden z-10 transition-all duration-200 animate-scale-in`}
        role="dialog"
        aria-modal="true"
      >
        {/* Top Navigation Bar */}
        <div className="px-4 sm:px-6 py-3.5 border-b border-surface-200 dark:border-surface-800 bg-surface-50/80 dark:bg-surface-850/80 backdrop-blur-sm flex items-center justify-between gap-3 shrink-0">
          {/* File Information */}
          <div className="flex items-center space-x-3 min-w-0 pr-2">
            <div className="w-10 h-10 rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 flex items-center justify-center shrink-0 shadow-xs">
              {getFileIcon()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <h3
                  className="text-xs sm:text-sm font-bold text-surface-900 dark:text-surface-100 truncate max-w-xs sm:max-w-md lg:max-w-lg"
                  title={fileName}
                >
                  {fileName}
                </h3>
                {ext && (
                  <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded-md bg-surface-200/80 dark:bg-surface-700 text-surface-700 dark:text-surface-300 shrink-0">
                    {ext}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-surface-400 dark:text-surface-400 truncate mt-0.5 flex items-center space-x-2">
                <span>{formatFileSize(fileSize)}</span>
                {file.uploaderName && (
                  <>
                    <span>•</span>
                    <span>Uploaded by {file.uploaderName}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Action Toolbar */}
          <div className="flex items-center space-x-1 sm:space-x-1.5 shrink-0">
            {/* Image Zoom & Rotation Controls */}
            {isImage && (
              <div className="hidden sm:flex items-center space-x-1 pr-2 border-r border-surface-200 dark:border-surface-700">
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.max(30, z - 20))}
                  className="p-1.5 text-surface-500 hover:text-surface-900 dark:hover:text-surface-100 rounded-lg hover:bg-surface-200/60 dark:hover:bg-surface-800 transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-[11px] font-mono text-surface-500 w-11 text-center font-medium">
                  {zoom}%
                </span>
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.min(300, z + 20))}
                  className="p-1.5 text-surface-500 hover:text-surface-900 dark:hover:text-surface-100 rounded-lg hover:bg-surface-200/60 dark:hover:bg-surface-800 transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setZoom(100);
                    setRotation(0);
                  }}
                  className="p-1.5 text-surface-500 hover:text-surface-900 dark:hover:text-surface-100 rounded-lg hover:bg-surface-200/60 dark:hover:bg-surface-800 transition-colors"
                  title="Reset View"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setRotation((r) => (r + 90) % 360)}
                  className="p-1.5 text-surface-500 hover:text-surface-900 dark:hover:text-surface-100 rounded-lg hover:bg-surface-200/60 dark:hover:bg-surface-800 transition-colors"
                  title="Rotate 90°"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* CSV View Switcher */}
            {isCsv && textContent && (
              <div className="flex items-center bg-surface-200/60 dark:bg-surface-800 p-0.5 rounded-xl text-xs font-semibold mr-1">
                <button
                  type="button"
                  onClick={() => setCsvViewMode('table')}
                  className={`px-2.5 py-1 rounded-lg flex items-center space-x-1 transition-all ${
                    csvViewMode === 'table'
                      ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-brand-400 shadow-xs'
                      : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-200'
                  }`}
                >
                  <Table className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Table</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCsvViewMode('raw')}
                  className={`px-2.5 py-1 rounded-lg flex items-center space-x-1 transition-all ${
                    csvViewMode === 'raw'
                      ? 'bg-white dark:bg-surface-700 text-brand-600 dark:text-brand-400 shadow-xs'
                      : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-200'
                  }`}
                >
                  <Code className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Raw</span>
                </button>
              </div>
            )}

            {/* Copy Button for Text-Like Files */}
            {isTextLike && textContent && (
              <button
                type="button"
                onClick={handleCopy}
                className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-xl border border-surface-200 dark:border-surface-700 hover:bg-white dark:hover:bg-surface-800 text-surface-600 dark:text-surface-300 font-semibold text-xs flex items-center space-x-1.5 transition-colors shadow-xs"
                title="Copy contents to clipboard"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="hidden sm:inline text-emerald-600 dark:text-emerald-400">Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 text-surface-500" />
                    <span className="hidden sm:inline">Copy</span>
                  </>
                )}
              </button>
            )}

            {/* Open in New Tab Button */}
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 sm:px-2.5 sm:py-1.5 rounded-xl border border-surface-200 dark:border-surface-700 hover:bg-white dark:hover:bg-surface-800 text-surface-600 dark:text-surface-300 font-semibold text-xs flex items-center space-x-1.5 transition-colors shadow-xs"
                title="Open document in a separate browser tab"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Open Tab</span>
              </a>
            )}

            {/* Download Button */}
            <a
              href={downloadUrl}
              download={fileName}
              className="px-2.5 sm:px-3 py-1.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs flex items-center space-x-1.5 transition-colors shadow-sm"
              title="Download file to computer"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Download</span>
            </a>

            {/* Fullscreen Toggle */}
            <button
              type="button"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 hover:bg-surface-200/60 dark:hover:bg-surface-800 rounded-xl transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-surface-400 hover:text-surface-700 dark:hover:text-surface-200 hover:bg-surface-200/60 dark:hover:bg-surface-800 rounded-xl transition-colors"
              aria-label="Close Preview"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Modal Main Viewing Area */}
        <div className="flex-1 min-h-0 bg-surface-100/70 dark:bg-surface-950/60 relative overflow-auto flex flex-col items-center justify-center">
          {/* 1. PDF Preview */}
          {isPdf ? (
            <div className="w-full h-full flex flex-col relative bg-surface-100 dark:bg-surface-950">
              <iframe
                src={`${previewUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                className="w-full h-full border-0 bg-surface-100 dark:bg-surface-900"
                title={fileName}
              />
              <div className="p-2 bg-surface-50 dark:bg-surface-900 border-t border-surface-200 dark:border-surface-800 flex items-center justify-between text-[11px] text-surface-500 shrink-0">
                <span>PDF Document Preview • Interactive Controls enabled</span>
                <div className="flex items-center space-x-3">
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 dark:text-brand-400 hover:underline flex items-center space-x-1"
                  >
                    <span>Having trouble viewing? Open full PDF tab</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          ) : isImage ? (
            /* 2. Image Preview with Zoom & Pan */
            <div className="w-full h-full flex items-center justify-center p-4 overflow-auto touch-pan-x touch-pan-y select-none">
              <div
                className="transition-transform duration-150 ease-out flex items-center justify-center"
                style={{
                  transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                }}
              >
                <img
                  src={previewUrl}
                  alt={fileName}
                  className="max-w-[85vw] max-h-[75vh] object-contain rounded-xl shadow-lg border border-surface-200/60 dark:border-surface-800"
                />
              </div>
            </div>
          ) : isCsv && csvViewMode === 'table' ? (
            /* 3. CSV Tabular Interactive Preview */
            <div className="w-full h-full flex flex-col bg-white dark:bg-surface-900">
              {/* Search Header inside CSV */}
              <div className="p-3 border-b border-surface-200 dark:border-surface-800 flex items-center justify-between gap-3 bg-surface-50/50 dark:bg-surface-850/50 shrink-0">
                <div className="relative w-64">
                  <Search className="w-3.5 h-3.5 text-surface-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search table rows..."
                    value={csvSearch}
                    onChange={(e) => setCsvSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-1.5 text-xs rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div className="text-[11px] text-surface-400">
                  {filteredCsvRows.length > 1
                    ? `${filteredCsvRows.length - 1} rows displayed`
                    : 'No matching rows'}
                </div>
              </div>

              {/* Table Data */}
              <div className="flex-1 overflow-auto p-4">
                {isLoadingText ? (
                  <div className="h-64 flex flex-col items-center justify-center text-surface-400 space-y-2">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-500" />
                    <span className="text-xs">Parsing CSV table data...</span>
                  </div>
                ) : filteredCsvRows.length > 0 ? (
                  <div className="border border-surface-200 dark:border-surface-700 rounded-xl overflow-hidden shadow-xs">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-surface-100/80 dark:bg-surface-800 border-b border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-200 font-bold sticky top-0 shadow-xs">
                          {filteredCsvRows[0].map((h, i) => (
                            <th key={i} className="px-3.5 py-2.5 border-r border-surface-200 dark:border-surface-700 last:border-0 truncate max-w-xs">
                              {h || `Column ${i + 1}`}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-surface-100 dark:divide-surface-800">
                        {filteredCsvRows.slice(1).map((row, rIdx) => (
                          <tr key={rIdx} className="hover:bg-brand-50/40 dark:hover:bg-brand-950/20 transition-colors">
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className="px-3.5 py-2 text-surface-800 dark:text-surface-200 border-r border-surface-100 dark:border-surface-800/80 last:border-0 font-mono text-[11px] truncate max-w-xs" title={cell}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-xs text-surface-400">No data found in this CSV.</div>
                )}
              </div>
            </div>
          ) : isTextLike ? (
            /* 4. Text / Code / JSON / Raw CSV Code Viewer */
            <div className="w-full h-full flex flex-col bg-surface-900 text-surface-100 font-mono text-xs overflow-auto">
              {isLoadingText ? (
                <div className="h-64 flex flex-col items-center justify-center text-surface-400 space-y-2">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-400" />
                  <span className="text-xs">Loading text content...</span>
                </div>
              ) : textError ? (
                <div className="p-8 text-center space-y-3">
                  <AlertCircle className="w-8 h-8 mx-auto text-rose-500" />
                  <p className="text-rose-400 font-semibold">{textError}</p>
                  <a
                    href={downloadUrl}
                    className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-surface-800 hover:bg-surface-700 rounded-xl text-white font-sans text-xs"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download file instead</span>
                  </a>
                </div>
              ) : (
                <div className="p-4 sm:p-6 overflow-auto">
                  <pre className="whitespace-pre-wrap break-words leading-relaxed selection:bg-brand-500/40">
                    {textContent}
                  </pre>
                </div>
              )}
            </div>
          ) : isVideo ? (
            /* 5. Video Player */
            <div className="w-full h-full flex items-center justify-center p-4 bg-black">
              <video
                src={previewUrl}
                controls
                className="max-w-full max-h-[75vh] rounded-xl shadow-2xl"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          ) : isAudio ? (
            /* 6. Audio Player */
            <div className="p-8 sm:p-12 max-w-md w-full bg-white dark:bg-surface-900 rounded-3xl border border-surface-200 dark:border-surface-800 shadow-xl text-center space-y-5">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-pink-50 dark:bg-pink-950/40 text-pink-600 dark:text-pink-400 flex items-center justify-center shadow-sm">
                <FileAudio className="w-8 h-8" />
              </div>
              <div>
                <h4 className="font-bold text-surface-900 dark:text-surface-100 text-sm">{fileName}</h4>
                <p className="text-xs text-surface-400 mt-1">{formatFileSize(fileSize)}</p>
              </div>
              <audio src={previewUrl} controls className="w-full" />
            </div>
          ) : (
            /* 7. Office / Binary / Packaged Documents Card */
            <div className="p-8 sm:p-12 max-w-lg w-full bg-white dark:bg-surface-900 rounded-3xl border border-surface-200 dark:border-surface-800 shadow-xl text-center space-y-6">
              <div className="w-20 h-20 mx-auto rounded-3xl bg-brand-50 dark:bg-brand-950/40 text-brand-600 dark:text-brand-400 flex items-center justify-center shadow-sm">
                {getFileIcon()}
              </div>

              <div>
                <h4 className="font-bold text-surface-900 dark:text-surface-100 text-base">{fileName}</h4>
                <p className="text-xs text-surface-500 dark:text-surface-400 mt-1">
                  {formatFileSize(fileSize)} • {ext.toUpperCase()} Document
                </p>
              </div>

              <div className="p-4 bg-surface-50 dark:bg-surface-800/60 rounded-2xl border border-surface-200 dark:border-surface-700/80 text-xs text-surface-600 dark:text-surface-300 text-left space-y-2">
                <div className="font-semibold text-surface-800 dark:text-surface-200 flex items-center space-x-1.5">
                  <Eye className="w-4 h-4 text-brand-500" />
                  <span>Document Viewing Options</span>
                </div>
                <p className="text-[11px] text-surface-500 dark:text-surface-400 leading-relaxed">
                  This file format ({ext.toUpperCase()}) can be downloaded directly to open in your native desktop application (Microsoft Word, Excel, PowerPoint, Acrobat, or Archive Manager).
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={downloadUrl}
                  download={fileName}
                  className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold text-xs flex items-center justify-center space-x-2 shadow-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Document</span>
                </a>
                {previewUrl && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-surface-200 dark:border-surface-700 hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-700 dark:text-surface-200 font-semibold text-xs flex items-center justify-center space-x-2 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Open in Browser</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
